import * as React from "react";
import { CPLN_HELM_RELEASE_LEGACY_PREFIX, linksOf, request, resourceLink } from "../../../../services/cpln";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { fetchLegacyReleaseItems, getMarketplaceErrorMessage } from "../../utils";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { BasePathContext, useBasePath } from "../../../../reactContexts/basePathContext";
import { MarketplaceReleaseInstance, LegacyReleaseState } from "../../types/release";
import { LegacyReleaseInfo } from "./legacyInfo";
import { LegacyReleaseUpgrade } from "./legacyUpgrade";
import { LegacyReleaseRevisions } from "./legacyRevisions";
import { Loader } from "../../../../components/layout/loader";
import { Alert } from "antd";
import { useAppSelector } from "../../../../store/hooks";
import { selectOrg } from "../../../../store/contextSlice";
import { TemplateReleaseDetailHeader } from "../../../workload/templateReleaseHeader";
import { ContentLayout } from "../../../../components/layout/contentLayout";
import { SingleNavMenu } from "../../../../components/antd/layout/singleNavMenu";
import { forPage, SingleNavMenuLink, toLinkPage } from "../../../identity/useNavMenu";

type RouteParams = "release";
export const MarketplaceLegacyReleaseDetail: React.FC = () => {
  const org = useAppSelector(selectOrg);

  const { release: releaseName } = useParams<RouteParams>();

  const basePathUpdated = useBasePath(`/marketplace/release/legacy/${releaseName}/*`);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [release, setRelease] = React.useState<MarketplaceReleaseInstance>(null as any);

  const [items, setItems] = React.useState<any[]>([]);
  const [itemsNotFound, setItemsNotFound] = React.useState<any[]>([]);
  const [state, setState] = React.useState<LegacyReleaseState>({ deployments: [], release: "", schemaVersion: "" });

  React.useEffect(() => {
    fetchRelease();
  }, []);

  React.useEffect(() => {
    if (!release) {
      return;
    }
    if (state.deployments.length < 1) {
      return;
    }

    fetchItems();
  }, [state]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    // TODO Get agent health
    // TODO Get volumeset info
  }, [items]);

  async function fetchRelease() {
    try {
      setIsLoading(true);
      const { data } = await request({
        url: resourceLink("secret", CPLN_HELM_RELEASE_LEGACY_PREFIX + releaseName) + "/-reveal",
      });

      const _state = JSON.parse(data.data.payload);
      setState(_state);

      const revision = _state.deployments.toSorted((a, b) => {
        if (a.revision > b.revision) return -1;
        if (b.revision > a.revision) return 1;
        return 0;
      })[0].revision;

      setRelease({
        kind: "marketplace-release",
        lastModified: data.lastModified,
        created: data.created,
        tags: data.tags,
        gvc: data.tags["console/marketplace-gvc"] || data.tags["cpln/marketplace-gvc"] || "",
        org: org,
        name: releaseName!,
        id: releaseName!,
        template: data.tags["console/marketplace-template"] || data.tags["cpln/marketplace-template"],
        version: data.tags["console/marketplace-template-version"] || data.tags["cpln/marketplace-template-version"],
        revision: revision,
      });
      setIsLoading(false);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchItems() {
    try {
      const revision = state.deployments.toSorted((a, b) => {
        if (a.revision > b.revision) return -1;
        if (b.revision > a.revision) return 1;
        return 0;
      })[0];
      const _itemResults = await fetchLegacyReleaseItems(revision.resources);
      const _itemsNotFound = _itemResults.filter((_i) => _i.notFound === true);
      const _items = _itemResults.filter((_i) => !_i.notFound);

      const kindOrder = [
        "gvc",
        "workload",
        "volumeset",
        "identity",
        "domain",
        "agent",
        "secret",
        "cloudaccount",
        "group",
        "serviceaccount",
        "policy",
        "auditctx",
      ];

      let _sortedItems: any[] = [];
      for (const kind of kindOrder) {
        _sortedItems = _sortedItems.concat(..._items.filter((i) => i.kind === kind));
      }
      setItems(_sortedItems);
      let _sortedItemsNotFound: any[] = [];
      for (const kind of kindOrder) {
        _sortedItemsNotFound = _sortedItemsNotFound.concat(..._itemsNotFound.filter((i) => i.kind === kind));
      }
      setItemsNotFound(_sortedItemsNotFound);
      setIsLoading(false);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
      setIsLoading(false);
    }
  }

  const usedGvcs: string[] = [];
  for (const item of items) {
    const { isValid, name: gvcName } = parseLink(linksOf(item).gvc || "", { useInputCtx: true });
    if (isValid && !usedGvcs.includes(gvcName)) {
      usedGvcs.push(gvcName);
    }
  }

  const links: SingleNavMenuLink[] = React.useMemo(() => {
    return [
      toLinkPage("Info", "/info"), //
      toLinkPage("Upgrade", "/upgrade"),
      toLinkPage("Revisions", "/revisions"),
    ];
  }, []);

  if (error) {
    return <Alert type={"error"} message="Error" description={`${error}`} />;
  }

  if (!release || isLoading) {
    return <Loader />;
  }

  return (
    <>
      <BasePathContext.Provider value={basePathUpdated}>
        <TemplateReleaseDetailHeader release={release} items={items} />
        <ContentLayout
          nav={<SingleNavMenu useDetailHeader={true} links={links} pagesWithScrolls={[]} />}
          content={
            <Routes>
              <Route index element={<Navigate to={`${basePathUpdated}/info`} replace />} />
              <Route
                path={`info/*`}
                element={
                  <LegacyReleaseInfo
                    release={release}
                    usedGvcs={usedGvcs}
                    items={items}
                    itemsNotFound={itemsNotFound}
                  />
                }
              />
              <Route path={`upgrade/*`} element={<LegacyReleaseUpgrade release={release} onRefresh={fetchRelease} />} />
              <Route
                path={`revisions/*`}
                element={<LegacyReleaseRevisions release={release} state={state} onRefresh={fetchRelease} />}
              />
            </Routes>
          }
        />
      </BasePathContext.Provider>
    </>
  );
};
